import { useState, useEffect, useMemo } from 'react';

/**
 * Custom hook to manage countdown timers for different round phases
 * 
 * Handles:
 * - Betting phase countdown
 * - Racing phase countdown
 * - Results phase countdown
 * 
 * @param currentRound - The current round object with phase and timestamps
 * @param connectionStatus - WebSocket connection status
 * @returns Object with timer values and betting enablement status
 */
export const useRoundTimers = (currentRound: any, connectionStatus: string) => {
  // Timer state
  const [bettingTimeRemaining, setBettingTimeRemaining] = useState<number | null>(null);
  const [racingTimeRemaining, setRacingTimeRemaining] = useState<number | null>(null);
  const [resultsTimeRemaining, setResultsTimeRemaining] = useState<number | null>(null);

  // Derived state
  const isBettingPhase = currentRound?.phase === 'BETTING';
  
  const isBettingEnabled = useMemo(() => {
    // Disable betting if WebSocket is disconnected
    const isConnected = connectionStatus === 'connected';
    return isBettingPhase && (bettingTimeRemaining == null || bettingTimeRemaining >= 1) && isConnected;
  }, [isBettingPhase, bettingTimeRemaining, connectionStatus]);

  // Betting phase countdown timer effect
  useEffect(() => {
    if (isBettingPhase && currentRound?.timestamps?.bettingEnd) {
      const updateBettingCountdown = () => {
        const now = Date.now();
        const bettingEndTime = new Date(currentRound.timestamps.bettingEnd).getTime();
        const timeRemaining = Math.max(0, Math.floor((bettingEndTime - now) / 1000));
        
        setBettingTimeRemaining(timeRemaining);
      };
      
      // Update immediately and then every second
      updateBettingCountdown();
      const interval = setInterval(updateBettingCountdown, 1000);
      
      return () => clearInterval(interval);
    } else {
      setBettingTimeRemaining(null);
    }
  }, [isBettingPhase, currentRound?.timestamps?.bettingEnd]);

  // Racing phase countdown timer effect
  useEffect(() => {
    if (currentRound?.phase === 'RACING' && currentRound?.timestamps?.raceEnd) {
      const updateRacingCountdown = () => {
        const now = Date.now();
        const raceEndTime = new Date(currentRound.timestamps.raceEnd).getTime();
        const timeRemaining = Math.max(0, Math.floor((raceEndTime - now) / 1000));
        
        // Debug logging to catch NaN issues
        if (isNaN(timeRemaining)) {
          console.error('❌ Racing countdown NaN detected:', {
            now,
            raceEndTimeString: currentRound.timestamps.raceEnd,
            raceEndTime,
            timeRemaining,
            calculation: raceEndTime - now
          });
        }
        
        setRacingTimeRemaining(timeRemaining);
      };
      
      // Update immediately and then every second
      updateRacingCountdown();
      const interval = setInterval(updateRacingCountdown, 1000);
      
      return () => clearInterval(interval);
    } else {
      setRacingTimeRemaining(null);
    }
  }, [currentRound?.phase, currentRound?.timestamps?.raceEnd]);

  // Results phase countdown timer effect
  useEffect(() => {
    if (currentRound?.phase === 'RESULTS' && currentRound?.timestamps?.resultsPhaseStart) {
      const updateResultsCountdown = () => {
        const now = Date.now();
        const resultsPhaseStartTime = currentRound.timestamps.resultsPhaseStart; // ✅ Use actual RESULTS phase start time
        const resultsPhaseDuration = 5000; // 5 seconds for results phase
        const resultsPhaseEndTime = resultsPhaseStartTime + resultsPhaseDuration;
        const timeRemaining = Math.max(0, Math.floor((resultsPhaseEndTime - now) / 1000));
        
        // Debug logging to catch NaN issues
        if (isNaN(timeRemaining)) {
          console.error('❌ Results countdown NaN detected:', {
            now,
            resultsPhaseStartTime,
            resultsPhaseEndTime,
            timeRemaining
          });
        }
        
        setResultsTimeRemaining(timeRemaining);
      };
      
      // Update immediately and then every second
      updateResultsCountdown();
      const interval = setInterval(updateResultsCountdown, 1000);
      
      return () => clearInterval(interval);
    } else {
      setResultsTimeRemaining(null);
    }
  }, [currentRound?.phase, currentRound?.timestamps?.resultsPhaseStart]);

  return {
    bettingTimeRemaining,
    racingTimeRemaining,
    resultsTimeRemaining,
    isBettingPhase,
    isBettingEnabled
  };
};

